import dynamic from 'next/dynamic';
import { useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import TrailPageType from '@alltrails/amplitude/enums/TrailPageType';
import logTrailDetailsActionTapped from '@alltrails/amplitude/events/logTrailDetailsActionTapped';
import useAuthorization from '@alltrails/context/hooks/useAuthorization';
import useUser from '@alltrails/context/hooks/useUser';
import { useLanguageRegionCode } from '@alltrails/core/hooks/useLanguageRegionCode';
import { wrapUrlSafe } from '@alltrails/core/utils/wrapUrlSafe';
import Button from '@alltrails/denali/components/Button';
import Gallery from '@alltrails/denali/icons/Gallery';
import type { TrailId } from '@alltrails/types/TrailId';

const SignUpModal = dynamic(() => import('@alltrails/user-auth/components/SignUpModal'), { ssr: false });

const messages = defineMessages({
  PHOTOS_BUTTON: { defaultMessage: '{photoCount, plural, one {# photo} other {# photos}}' }
});

type PhotosButtonProps = {
  photoCount: number;
  trailId: TrailId;
  trailPageType: TrailPageType;
  trailSlug: string;
};

const PhotosButton = ({ photoCount, trailId, trailPageType, trailSlug }: PhotosButtonProps) => {
  const intl = useIntl();
  const languageRegionCode = useLanguageRegionCode();
  const { hasPermission } = useAuthorization();
  const [isSignupModalOpen, setIsSignupModalOpen] = useState(false);
  const user = useUser();

  const photosButtonText = intl.formatMessage(messages.PHOTOS_BUTTON, { photoCount });

  return (
    <>
      <Button
        icon={{ Component: Gallery }}
        {...(user
          ? {
              linkInfo: {
                href: hasPermission({ permission: 'trails:manage' }) ? `/contribute/admin/photos?trail_id=${trailId}` : `/trail/${trailSlug}/photos`,
                ...(hasPermission({ permission: 'trails:manage' }) ? { target: '_blank' } : {})
              }
            }
          : {})}
        onClick={() => {
          logTrailDetailsActionTapped({
            trail_id: trailId.toString(),
            trail_page_action: 'view photos',
            trail_page_location: 'hero image inset',
            trail_page_type: trailPageType
          });

          if (!user) {
            setIsSignupModalOpen(true);
          }
        }}
        text={photosButtonText}
        variant="tertiary"
      />
      {isSignupModalOpen && (
        <SignUpModal
          isOpen={isSignupModalOpen}
          closeModal={() => {
            setIsSignupModalOpen(false);
          }}
          deeplinkData={{ pageId: trailId, pageType: 'trail' }}
          onSuccess={() => {
            setIsSignupModalOpen(false);

            if (hasPermission({ permission: 'trails:manage' })) {
              window.open(wrapUrlSafe(`/contribute/admin/photos?trail_id=${trailId}`, languageRegionCode), '_blank');
            } else {
              window.location.assign(wrapUrlSafe(`/trail/${trailSlug}/photos`, languageRegionCode));
            }
          }}
          trigger="See More Photos"
          upsellTrigger="See More Photos"
        />
      )}
    </>
  );
};

export default PhotosButton;
